import React, { useEffect, useRef } from "react";
import { FiLogOut, FiHelpCircle } from "react-icons/fi";
import MenuTerms from "newui/features/AppMenu/components/MenuTerms";
import logo from "../assets/logo.png";
import logoText from "../assets/logo-procuracon.svg";
import Tooltip from "components/Tooltip";

interface AppMenuFooterProps {
  isCollapsed: boolean;
  showHelp?: boolean;
  onLogout: () => void;
  onHelpClick?: () => void;
}

/**
 * Moves the Userback widget (#userback_button_container) into the sidebar
 * so it scrolls with the menu instead of floating over content.
 */
const useUserbackInSidebar = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  isCollapsed: boolean,
) => {
  useEffect(() => {
    const target = containerRef.current;
    if (!target) {
      return;
    }

    const moveWidget = () => {
      const widget = document.getElementById("userback_button_container");
      if (!widget) {
        return;
      }

      // Move into our container if not already there
      if (widget.parentElement !== target) {
        widget.style.position = "relative";
        widget.style.top = "auto";
        widget.style.bottom = "auto";
        widget.style.right = "auto";
        widget.style.left = "auto";
        widget.style.zIndex = "auto";
        target.appendChild(widget);
      }

      // Show/hide based on collapsed state
      widget.style.display = isCollapsed ? "none" : "";
    };

    // The Userback SDK injects the widget asynchronously — observe for it
    moveWidget();
    const observer = new MutationObserver(moveWidget);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [containerRef, isCollapsed]);
};

export const AppMenuFooter: React.FC<AppMenuFooterProps> = ({
  isCollapsed,
  onLogout,
  onHelpClick,
  showHelp = false,
}) => {
  const userbackRef = useRef<HTMLDivElement>(null);
  useUserbackInSidebar(userbackRef, isCollapsed);

  return (
    <div>
      {/* Separator */}
      <div
        className="sidemuchild mx-2 mb-2"
        style={{ borderBottom: isCollapsed ? "solid 1px rgba(255,255,255,0.2)" : "solid 1px #EAECF0" }}
      />

      {/* Logout button */}
      {isCollapsed ? (
        <Tooltip label="Logout" placement="right" hasArrow shouldWrapChildren={false}>
          <button
            onClick={onLogout}
            className="flex w-full items-center justify-center py-3"
            data-testid="logout-button"
          >
            <FiLogOut size={20} color="#fff" />
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={onLogout}
          className="group flex w-full items-center gap-3 px-6 py-3 hover:bg-utility-blue-light-50"
          data-testid="logout-button"
        >
          <FiLogOut size={20} color="#0045F5" />
          <span
            className="sidemuchild font-roboto group-hover:text-royal-blue"
            style={{ fontWeight: 500, lineHeight: "24px" }}
          >
            Logout
          </span>
        </button>
      )}

      {!isCollapsed && (
        <div
          className="sidemuchild mx-2 mb-2"
          style={{ borderBottom: "solid 1px #EAECF0" }}
        />
      )}

      {/*
        Logo + Userback + Help section.
        Both expanded and collapsed views are ALWAYS rendered (using hidden class to toggle)
        so the Userback widget DOM node is never destroyed by React unmounting.
      */}
      <div className={isCollapsed ? "hidden" : "flex items-center justify-between px-5 pb-3 pt-6"}>
        <img
          src={logoText}
          alt="Procuracon Logo text"
          className="sidemuchild logolong"
          style={{ width: "154px", height: "24px" }}
        />
        <div className="flex items-center gap-2">
          <div
            ref={userbackRef}
            className="flex items-center"
            data-testid="userback-sidebar-container"
          />
          {showHelp && onHelpClick && (
            <Tooltip label="Restart Guided Tour" placement="right" hasArrow shouldWrapChildren={false}>
              <button
                onClick={onHelpClick}
                className="bg-Colors-Brand-25/70 inline-flex h-8 items-center justify-start gap-2.5 rounded-full p-2"
                aria-label="Guided Tour"
              >
                <div className="GuidedTourIcon relative h-5 w-5 rounded-full hover:cursor-pointer hover:text-royal-blue">
                  <FiHelpCircle size={20} />
                </div>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={isCollapsed ? "flex flex-col items-center gap-2 py-2" : "hidden"}>
        {showHelp && onHelpClick && (
          <Tooltip label="Restart Guided Tour" placement="right" hasArrow shouldWrapChildren={false}>
            <button
              onClick={onHelpClick}
              className="inline-flex h-8 items-center justify-center rounded-full p-2"
              aria-label="Guided Tour"
            >
              <FiHelpCircle size={20} color="#fff" />
            </button>
          </Tooltip>
        )}
        <img
          src={logo}
          alt="Procuracon Logo"
          width="36"
          height="36"
        />
      </div>

      {!isCollapsed && <MenuTerms />}
    </div>
  );
};
